/**
 * Authentication Utilities
 * 
 * Centralized authentication logic used across all protected pages.
 * Handles session-based authentication and redirects.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for session-based authentication with page-specific token validation
 * @param {string} redirectPath - Path to redirect to after successful login
 * @returns {object} - Authentication state and loading status
 */
export const useAuth = (redirectPath) => {
  const [isValidAccess, setIsValidAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const validateAccess = () => {
      const isAuthenticated = sessionStorage.getItem('wedding_authenticated');
      const authType = sessionStorage.getItem('wedding_auth_type');
      
      if (!isAuthenticated) {
        // User accessing without authentication, redirect to login
        navigate('/?redirect=' + btoa(redirectPath));
        return;
      }
      
      // If admin access, only allow fullAgenda page
      if (authType === 'admin') {
        // Only allow access to fullAgenda page
        if (redirectPath === '/fllagnd8293kxm') {
          setIsValidAccess(true);
          setIsLoading(false);
          return;
        } else {
          // Block access to all other pages
          navigate('/?error=access_denied');
          return;
        }
      }
      
      // If super admin access, allow all pages
      if (authType === 'super_admin') {
        setIsValidAccess(true);
        setIsLoading(false);
        return;
      }
      
      // If link admin access, only allow createlink page
      if (authType === 'link_admin') {
        if (redirectPath === '/createlink') {
          setIsValidAccess(true);
          setIsLoading(false);
          return;
        } else {
          // Block access to all other pages
          navigate('/?error=access_denied');
          return;
        }
      }
      
      // For guest tokens, validate page-specific access
      const urlParams = new URLSearchParams(window.location.search);
      const encodedRedirect = urlParams.get('redirect');
      
      if (!encodedRedirect) {
        // No redirect parameter means direct access - block for guest tokens
        setIsValidAccess(false);
        setIsLoading(false);
        navigate('/?error=missing_redirect');
        return;
      }
      
      try {
        const expectedPath = atob(encodedRedirect);
        const actualPath = window.location.pathname;
        
        if (expectedPath !== actualPath) {
          // Token is for a different page - block access
          setIsValidAccess(false);
          setIsLoading(false);
          navigate('/?error=invalid_token_page');
          return;
        }
      } catch (e) {
        // Invalid base64 encoding
        setIsValidAccess(false);
        setIsLoading(false);
        navigate('/?error=invalid_redirect');
        return;
      }
      
      // User is authenticated and page matches token, allow access
      setIsValidAccess(true);
      setIsLoading(false);
    };

    validateAccess();
  }, [navigate, redirectPath]);

  return { isValidAccess, isLoading };
};

/**
 * Special auth hook for PrivateAgenda page - only allows Super Admin access
 * @param {string} redirectPath - Path to redirect to after successful login
 * @returns {object} - Authentication state and loading status
 */
export const useSuperAdminAuth = (redirectPath) => {
  const [isValidAccess, setIsValidAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const validateSuperAdminAccess = () => {
      const isAuthenticated = sessionStorage.getItem('wedding_authenticated');
      const authType = sessionStorage.getItem('wedding_auth_type');
      
      if (!isAuthenticated) {
        // User accessing without authentication, redirect to login
        navigate('/?redirect=' + btoa(redirectPath));
        return;
      }
      
      // Only allow super_admin access to privateAgenda
      if (authType === 'super_admin') {
        setIsValidAccess(true);
        setIsLoading(false);
        return;
      }
      
      // All other users (including regular admin and guests) are blocked
      setIsValidAccess(false);
      setIsLoading(false);
      navigate('/?error=access_denied');
    };

    validateSuperAdminAccess();
  }, [navigate, redirectPath]);

  return { isValidAccess, isLoading };
};

/**
 * Special auth hook for CreateLink page - allows regular admin, super admin, and link admin
 * @param {string} redirectPath - Path to redirect to after successful login
 * @returns {object} - Authentication state and loading status
 */
export const useCreateLinkAuth = (redirectPath) => {
  const [isValidAccess, setIsValidAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const validateCreateLinkAccess = () => {
      const isAuthenticated = sessionStorage.getItem('wedding_authenticated');
      const authType = sessionStorage.getItem('wedding_auth_type');
      
      if (!isAuthenticated) {
        // User accessing without authentication, redirect to login
        navigate('/?redirect=' + btoa(redirectPath));
        return;
      }
      
      // Allow super_admin and link_admin access to CreateLink (exclude regular admin)
      if (authType === 'super_admin' || authType === 'link_admin') {
        setIsValidAccess(true);
        setIsLoading(false);
        return;
      }
      
      // All other users (including guests) are blocked
      setIsValidAccess(false);
      setIsLoading(false);
      navigate('/?error=access_denied');
    };

    validateCreateLinkAccess();
  }, [navigate, redirectPath]);

  return { isValidAccess, isLoading };
};

/**
 * Loading component for protected pages
 * @param {boolean} isLoading - Whether the page is loading
 * @param {boolean} isValidAccess - Whether user has valid access
 * @returns {JSX.Element|null} - Loading component or null
 */
export const AuthGuard = ({ isLoading, isValidAccess, children }) => {
  if (isLoading || !isValidAccess) {
    return (
      <div className="app">
        <div className="hero">
          <div className="hero-text">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>{isLoading ? 'Validating access...' : 'Access denied'}</p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

/**
 * Utility functions for session management
 */
export const setAuthSession = (guestName = 'Guest', authType = 'guest') => {
  sessionStorage.setItem('wedding_authenticated', 'true');
  sessionStorage.setItem('wedding_guest_name', guestName);
  sessionStorage.setItem('wedding_auth_type', authType);
};

export const clearAuthSession = () => {
  sessionStorage.removeItem('wedding_authenticated');
  sessionStorage.removeItem('wedding_guest_name');
  sessionStorage.removeItem('wedding_auth_type');
};

export const getGuestName = () => {
  return sessionStorage.getItem('wedding_guest_name') || 'Guest';
};

export const isAuthenticated = () => {
  return sessionStorage.getItem('wedding_authenticated') === 'true';
};